import { formatMoney } from '@shopify/theme-currency';
import { handleize, escapeHtml } from '../../core/helpers';
import { getSizedImageURL } from '../../core/images';
import getProductURL from '../../core/productURL';

const CONSTANTS = {
  MONEY_FORMAT: theme.moneyFormat,
  DEFAULT_ALT_TEXT: {
    FRONT: 'Front of',
    BACK: 'Back of',
    WITH_COLOR: 'with color',
  }
};

/* Different promo message for logged-in/loyalty and guest customers */
const promoForTile = (product, tile_settings) => {
  const is_loyalty_member = (typeof window.theme.customer.id !== 'undefined');
  let final_promo_html = '';

  /**************************************/
  /************ Build Values ************/
  /**************************************/

  // Promo applied by product tag or collection. Ignored when a promotion call out message has been defined for all products
  let promo_message = window.theme.promoMessage.guests;
  let promo_message_color = window.theme.promoMessage.color;
  let promo_discount = window.theme.promoMessage.guestPercentOff;
  let promo_discount_color = window.theme.promoMessage.colorPercentOff;
  let promo_tag = window.theme.promoMessage.tag;
  let promo_collection = window.theme.promoMessage.collection;
  let special_message = '';

  // Promo applied to all products.
  if (window.theme.promoMessage.guestsAllProducts !== '') {
    promo_message = window.theme.promoMessage.guestsAllProducts;
    promo_message_color = window.theme.promoMessage.colorAllProducts;
  }

  if (window.theme.promoMessage.guestAllProductsPercentOff !== 0) {
    promo_discount = window.theme.promoMessage.guestAllProductsPercentOff;
    promo_discount_color = window.theme.promoMessage.colorAllProductsPercentOff;
  }

  if (window.theme.promoMessage.guestsAllProducts !== '' || window.theme.promoMessage.guestAllProductsPercentOff !== 0) {
    promo_tag = '';
    promo_collection = '';
  }

  // Promo applied to loyalty members
  if (is_loyalty_member) {
    promo_message = window.theme.promoMessage.loyalty;
    promo_discount = window.theme.promoMessage.loyaltyPercentOff;
    promo_discount_color = window.theme.promoMessage.colorPercentOff;

    // Promo applied to all products.
    if (window.theme.promoMessage.loyaltyAllProducts !== '') {
      promo_message = window.theme.promoMessage.loyaltyAllProducts;
      promo_message_color = window.theme.promoMessage.colorAllProducts;
    }

    if (window.theme.promoMessage.loyaltyAllProductsPercentOff !== 0) {
      promo_discount = window.theme.promoMessage.loyaltyAllProductsPercentOff;
      promo_discount_color = window.theme.promoMessage.colorAllProductsPercentOff;
    }

    if (window.theme.promoMessage.loyaltyAllProducts !== '' || window.theme.promoMessage.loyaltyAllProductsPercentOff !== 0) {
      promo_tag = '';
      promo_collection = '';
    }
  }

  // Special message
  if (window.theme.promoMessage.specialMessage !== '') {
    if (is_loyalty_member) {
      if (window.theme.promoMessage.specialMessageCustomers === 'guests' || window.theme.promoMessage.specialMessageCustomers === 'all') {
        special_message = window.theme.promoMessage.specialMessage;
      }
    } else if (window.theme.promoMessage.specialMessageCustomers === 'members' || window.theme.promoMessage.specialMessageCustomers === 'all') {
      special_message = window.theme.promoMessage.specialMessage;
    }
  }

  // Escape HTML to prevent XSS (Cross-Site Scripting) attacks if the values are not properly sanitized.
  promo_message = escapeHtml(promo_message);
  special_message = escapeHtml(special_message);

  /**************************************/
  /************* Build HTML *************/
  /**************************************/
  // Discount message
  let discount_message_html = '';
  if (promo_discount && promo_discount !== 0 && (product.from === '' || typeof product.from === 'undefined')) {
    discount_message_html = `<p class="product-tile__price-promo" data-product-tile-price-promo data-raw-price="${product.price_text}" data-promo-discount="${promo_discount}"><span class="price__promo" style="color: ${promo_discount_color} !important;">${formatMoney(String((product.price - ((product.price * promo_discount) / 100)).toFixed(2)), CONSTANTS.MONEY_FORMAT )} after ${promo_discount}% off</span></p>`;
  }

  // Special message, we need to make sure it only applies to the selected collection if any
  let special_message_html = '';
  if (special_message !== '') {
    let show_special_message = false;

    if (window.theme.promoMessage.specialMessageCollection === '') {
      show_special_message = true;
    } else {
      if (tile_settings.is_search === false && document.querySelector('[data-collection-grid]') !== null) {
        if (window.theme.promoMessage.specialMessageCollection === document.querySelector('[data-collection-grid]').dataset.collection) {
          show_special_message = true;
        }
      }

      if (show_special_message === false && typeof product.categories !== 'undefined' && product.categories.length > 0) {
        for (let i = 0; i < product.categories.length; i++) {
          const collection = handleize(product.categories[i]);

          if (collection === window.theme.promoMessage.specialMessageCollection) {
            show_special_message = true;
            break;
          }
        }
      }
    }

    // console.log('show_special_message', show_special_message);
    if (show_special_message === true) {
      special_message_html = `<span class="product-tile__special-message" data-same-line="${window.theme.promoMessage.specialMessageSameLine}">${special_message}</span>`;
    }
  }

  // Promo message
  let promo_message_html = '';
  if (promo_message !== '') {
    promo_message_html = `<p data-product-tile-message class="product-tile__message" style="color: ${promo_message_color} !important;" tabindex="0"><span>${promo_message}</span>${special_message_html}</p>`;
  }

  if (promo_tag === '' && promo_collection === '') {
    // Message for all products
    final_promo_html += discount_message_html;
    final_promo_html += promo_message_html;

    if (promo_message_html === '' && special_message_html !== '') {
      final_promo_html += `<p data-product-tile-message class="product-tile__message" style="color: ${promo_message_color} !important;" tabindex="0">${special_message_html}</p>`;
    }
  } else {
    // Message for specific tagged products or collections
    let show_promo_message = false;

    if (promo_tag !== '' && promo_collection === '') {
      for (let i = 0; i < product_tags.length; i++) {
        const tag = product_tags[i];
        if (tag.indexOf(promo_tag) !== -1) {
          show_promo_message = true;
          break;
        }
      }
    } else {
      if (tile_settings.is_search === false && document.querySelector('[data-collection-grid]') !== null) {
        if (promo_collection === document.querySelector('[data-collection-grid]').dataset.collection) {
          show_promo_message = true;
        }
      }

      if (show_promo_message === false && typeof product.categories !== 'undefined' && product.categories.length > 0) {
        for (let i = 0; i < product.categories.length; i++) {
          const collection = handleize(product.categories[i]);

          if (collection === promo_collection) {
            show_promo_message = true;
            break;
          }
        }
      }
    }
    
    // console.log('show_promo_message', show_promo_message);
    if (show_promo_message === true) {
      final_promo_html += discount_message_html;
      final_promo_html += promo_message_html;

      if (promo_message_html === '' && special_message_html !== '') {
        final_promo_html += `<p data-product-tile-message class="product-tile__message" style="color: ${promo_message_color} !important;" tabindex="0">${special_message_html}</p>`;
      }
    } else if (special_message_html !== '') {
      final_promo_html += `<p data-product-tile-message class="product-tile__message" style="color:${promo_message_color} !important;" tabindex="0">${special_message_html}</p>`;
    }
  }

  return final_promo_html;
};

/*
  Flags based on tags.
  Limit 1 per tile.
  Priority: Best Seller app flag > Trending app flag > Style metafield flag > Tag-based flag
*/
const flagsForTile = (product, product_tags) => {
  let html = '';

  // Flags based on metafields
  let has_flag = false;
  if (typeof product.custom_fields !== 'undefined') {
    if (typeof product?.custom_fields?.bestseller_style_30 !== 'undefined' && parseInt(product?.custom_fields?.bestseller_style_30) <= 30) {
      html += `<div class="product-tile__flag product-tile__flag--app">Best Seller</div>`;
      has_flag = true;
    }

    if (typeof product?.custom_fields?.bestseller_style_14 !== 'undefined' && parseInt(product?.custom_fields?.bestseller_style_14) <= 30) {
      html += `<div class="product-tile__flag product-tile__flag--app">Trending</div>`;
      has_flag = true;
    }
  }

  // Tag-based flag
  // We don't show the final sale tags anymore, but they are needed to add a cart note
  if (has_flag === false) {
    product_tags.forEach((tag) => {
      if (tag.indexOf('flag:') !== -1 && has_flag === false) {
        html += `<div class="product-tile__flag${(tag.toLowerCase().includes('final sale')) ? ' product-tile__flag--final' : ' product-tile__flag--tag'}">${tag.replace('flag:', '')}</div>`;
        has_flag = !(tag.toLowerCase().includes('final sale'));
      }
    });
  }

  return `<div class="product-tile__flags">${html}</div>`;
};

const wishlistForTile = (product, product_handle) => {
  const is_loyalty_member = (typeof window.theme.customer.id !== 'undefined');

  return (!is_loyalty_member) ? `<button type="button" class="swym-button swym-add-to-wishlist-view-product product_${product.product_id} swym-heart swym-loaded" data-product-swym-wishlist data-product-handle="${product_handle}" data-open-signin-actions-modal="wishlist" aria-label="Add to Wishlist"></button>` : `<button data-with-epi="true" type="button" class="swym-button swym-add-to-wishlist-view-product product_${product.product_id}" data-product-swym-wishlist data-swaction="addToWishlist" data-product-id="${product.product_id}" data-variant-id="${product.skus[0].id}" data-product-url="${product.url}" data-product-handle="${product_handle}" aria-label="Add to Wishlist"></button>`;
};

const imagesForTile = (product, safe_product_title, tile_settings) => {
  const placeholder = window.theme.collections.placeholder;

  let images_html = `<div class="product-tile__image--placeholder"><img src="${placeholder}" alt="${safe_product_title}" width="300" height="460" /></div>`;

  if ((typeof product.swatches !== 'undefined' && product.swatches.length) || typeof product.image_url === 'undefined') {
    images_html += `<div class="product-tile__image--main" data-product-main-image><img loading="lazy" src="${placeholder}" alt="${safe_product_title}" width="300" height="460" /></div>`;

    if (window.theme.breakpoints.current !== 'mobile') {
      images_html += `<div class="product-tile__image--alt" data-product-alt-image><img loading="lazy" src="${placeholder}" alt="${safe_product_title}" width="300" height="460" /></div>`;
    }
  } else {
    // images_html += `<div class="product-tile__image--main" data-product-main-image><img loading="lazy" src="${product.image_url}" width="${product.image_width}" alt="${safe_product_title}" onError="this.src = '${placeholder}';" /></div>`;

    // We have 2 slides on mobile (use 100vw for double size)
    // We have 5 slides on desktop (use 40vw for double size)
    images_html += `
      <div class="product-tile__image--main" data-product-main-image>
        <img
          loading="lazy"
          src="$${getSizedImageURL(product.image_url, '800x')}"
          srcset="
            ${getSizedImageURL(product.image_url, '800x')} 800w,
            ${getSizedImageURL(product.image_url, '1024x')} 1024w,
            ${getSizedImageURL(product.image_url, '1280x')} 1280w,
            ${getSizedImageURL(product.image_url, '1600x')} 1600w,
            ${getSizedImageURL(product.image_url, '2000x')} 2000w"
          "
          sizes="(max-width: 768px) 100vw,
                40vw"
          alt="${safe_product_title}"
          onError="this.src = '${placeholder}';"
        />
      </div>
    `;

    if (window.theme.breakpoints.current !== 'mobile') {
      // images_html += `<div class="product-tile__image--alt" data-product-alt-image><img loading="lazy" src="${product.image_url}" width="${product.image_width}" alt="${safe_product_title}" onError="this.src = '${placeholder}';" /></div>`;

      // We have 2 slides on mobile (use 100vw for double size)
      // We have 5 slides on desktop (use 40vw for double size)
      images_html += `
        <div class="product-tile__image--alt" data-product-alt-image>
          <img
            loading="lazy"
            src="${getSizedImageURL(product.image_url, '800x')}"
            srcset="
              ${getSizedImageURL(product.image_url, '800x')} 800w,
              ${getSizedImageURL(product.image_url, '1024x')} 1024w,
              ${getSizedImageURL(product.image_url, '1280x')} 1280w,
              ${getSizedImageURL(product.image_url, '1600x')} 1600w,
              ${getSizedImageURL(product.image_url, '2000x')} 2000w"
            "
            sizes="(max-width: 768px) 100vw, 40vw"
            alt="${safe_product_title}"
            onError="this.src = '${placeholder}';"
          />
        </div>
      `;
    }
  }

  if (tile_settings.is_simplified === true && tile_settings.has_quickview === false) {
    images_html += `<div class="product-tile__shop">SHOP NOW</div>`;
  }

  return images_html;
};

// Disabled, functionality might not be working if re-enabled
const addToCartAltForTile = (product) => {
  return '';

  // return `<div class="add-to-cart-alt add-to-cart-alt--desktop btn btn--primary<% if (!product.available) { %> unavailable<% } %>" data-add-to-cart-alt-button role="button" tabindex="0" aria-label="Show product options"><span><%= window.theme.strings.addToCart %></span></div><div class="add-to-cart-alt add-to-cart-alt--mobile<% if (!product.available) { %> unavailable<% } %>" data-add-to-cart-alt-button><span><%= window.theme.strings.addToCart %></span></div>`;
};

const quickviewForTile = () => {
  return `<div class="quicklook__button btn btn--secondary hidden--mobile" data-quicklook-button data-modal="#quicklook-modal"><span>${window.theme.strings.quickView}</span></div><div class="quicklook__button--mobile" data-quicklook-button-mobile><span>${window.theme.strings.quickView}</span></div>`;
};

// We use a metafield to find out which is the back image.
// This is a fallback function; On PLPs, Nosto returns the recommended variant first, which is not always the first variant in the list and might not return the correct back image
const findBackVariantImage = (product, color_handle) => {
  let variant_back_image = '';

  // Find the next variant with a different color
  let found_same_color = false;
  for (let i = 0; i < product.skus.length; i++) {
    const find_variant = product.skus[i];
    const find_color_value = find_variant.custom_fields.Color;
    const find_color_handle = handleize(find_color_value);

    if (find_color_handle === color_handle) {
      found_same_color = true;
      continue;
    } else if (found_same_color && variant_back_image === '') {
      const next_variant_with_different_color = product.skus[i];
      const next_front_image = next_variant_with_different_color.image_url;
      const next_front_image_index = product.skus.findIndex(sku => sku.image_url === next_front_image);

      if (typeof product.alternate_image_urls[next_front_image_index - 1] !== 'undefined') {
        variant_back_image = product.alternate_image_urls[next_front_image_index - 1];
      }
      
      found_same_color = false;
      break;
    }
  }

  // console.log(product.name, color_handle, variant_back_image);

  // Last color variant
  if (found_same_color && variant_back_image === '') {
    variant_back_image = product.alternate_image_urls[product.alternate_image_urls.length - 1];
  }

  return variant_back_image;
};

const swatchesForTile = (product) => {
  // Swatches metafield format
  // [
  //   {
  //       "color_name": "Black",
  //       "color_handle": "black",
  //       "swatch_value": "#0e0e0e",
  //       "swatch_type": "hex"
  //   },
  //   {
  //       "color_name": "Moss",
  //       "color_handle": "moss",
  //       "swatch_value": "https://cdn.shopify.com/s/files/1/2483/6632/t/75/assets/astr-swatch--moss-4bdb0f3e-2c14-4ca0-976c-3f1dc07850d6.png?v=1726505940",
  //       "swatch_type": "image"
  //   }
  // ]

  let swatches_json = [];
  if (typeof product.custom_fields !== 'undefined' && typeof product.custom_fields['astr-swatches'] !== 'undefined') {
    swatches_json = JSON.parse(product.custom_fields['astr-swatches']);
  }

  let images_details = [];
  if (typeof product?.custom_fields['astr-images_details'] !== 'undefined') {
    images_details = JSON.parse(product.custom_fields['astr-images_details']);
  }
  
  const product_color_names = product.skus.map((sku) => sku.custom_fields.Color );
  let unique_product_color_names = [...new Set(product_color_names)];

  // We need to hide the non-plus size swatches if we are on a plus size PLP
  // Example: https://www.astrthelabel.com/collections/plus-size-dresses
  if (document.querySelector('.main-collection [data-collection-grid]')?.dataset?.plus === 'true') {
    const unique_product_color_names_plus_sizes = [];
    unique_product_color_names.forEach((color_name) => {
      const color_value = product.skus.find((sku) => sku.custom_fields.Color === color_name);
      // if (typeof color_value?.custom_fields['astr-plus_size'] !== 'undefined' && color_value?.custom_fields['astr-plus_size'] === 'true') {
      if (color_value.name.includes('Plus Size -')) {
        unique_product_color_names_plus_sizes.push(color_name);
      }
    });

    unique_product_color_names = unique_product_color_names_plus_sizes;
  }

  const unique_product_color_handles = unique_product_color_names.map((color) => handleize(color));

  // Nosto sometimes returns "recommended_sku" for recommendations only (Not collections or search results), we'll use it to init the correct color swatch
  let recommended_color = '';
  if (typeof product.recommended_sku !== 'undefined') {
    recommended_color = product.recommended_sku.name.split(' / ')[0];
  }

  let swatch_items_html = '';
  let total_swatches = 0;
  unique_product_color_names.forEach((color, index) => {
    const color_handle = unique_product_color_handles[index];

    // Find the 1st variant using this color
    const same_color_variants = product.skus.filter((sku) => sku.custom_fields.Color === color);
    const first_variant = same_color_variants[0];

    // Extract the images data from the metafield
    let front_image_url = '';
    let front_image_alt = '';
    let back_image_url = '';
    let back_image_alt = '';

    images_details.find((image) => {
      if (image.color_name === color) {
        front_image_url = first_variant.image_url;
        front_image_alt = (image.front_image_alt === '') ? `${CONSTANTS.DEFAULT_ALT_TEXT.FRONT} ${product.name} ${CONSTANTS.DEFAULT_ALT_TEXT.WITH_COLOR} ${color}` : image.front_image_alt;
        back_image_url = (image.back_image_url === '') ? findBackVariantImage(product, color_handle) : image.back_image_url;
        back_image_alt = (image.back_image_alt === '') ? `${CONSTANTS.DEFAULT_ALT_TEXT.BACK} ${product.name} ${CONSTANTS.DEFAULT_ALT_TEXT.WITH_COLOR} ${color}` : image.back_image_alt;
      }
    });

    // Find color fill
    let swatch_json = swatches_json.find((swatch) => swatch.color_handle === color_handle);
    if (typeof swatch_json === 'undefined' && color_handle.includes('plus-size-')) {
      swatch_json = swatches_json.find((swatch) => swatch.color_handle === color_handle.replace('plus-size-', ''));
    }

    let swatch_span = `<span style="background-color: #FFFFFF;"></span>`;
    if (typeof swatch_json !== 'undefined') {
      swatch_span = (swatch_json.swatch_type === 'image') ? `<span><img loading="lazy" src="${escapeHtml(swatch_json.swatch_value)}" alt="${escapeHtml(color)} color swatch" height="36" width="36" /></span>` : `<span style="background-color: ${escapeHtml(swatch_json.swatch_value)};"></span>`;
    }

    swatch_items_html += `
      <div
        data-color-swatch
        class="color-swatch"
        role="button"
        aria-label="Select ${escapeHtml(color)} color"
        tabindex="0"
        data-variant-id="${first_variant.id}"
        data-color-value="${escapeHtml(color)}"
        data-color-handle="${color_handle}"
        data-front="${front_image_url}"
        data-back="${back_image_url}"
        data-front-alt="${front_image_alt}"
        data-back-alt="${back_image_alt}"
        data-is-recommended="${(color === recommended_color) ? 'true' : 'false'}"
      >
        ${swatch_span}
      </div>`;

    total_swatches++;
  });

  const swatches_html = `
    <div class="product-tile__colors" data-product-tile-colors>
      <div class="color-swatches" data-color-swatches>
        ${swatch_items_html}
      </div>
      <a href="${product.url}" aria-label="More Colors +" tabindex="0" class="color-swatch__more" data-total-swatches="${total_swatches}"><span>More Colors +</span></a>
    </div>
  `;

  return swatches_html;
};

const ratingsForTile = (product) => {
  /*
  // Find out if one of the skus has the stamped.reviews_average metafield available and get the 1st one available
  let ratings_html = '';
  let reviews_average = null;

  for (let i = 0; i < product.skus.length; i++) {
    const sku = product.skus[i];

    if (typeof sku.custom_fields['stamped-reviews_average'] !== 'undefined' && sku.custom_fields['stamped-reviews_average'] !== null && sku.custom_fields['stamped-reviews_average'] !== '') {
      reviews_average = sku.custom_fields['stamped-reviews_average'];
      break;
    }
  }
  */
  // Rating is now provided at product level with Nosto
  let reviews_average = null;

  if (typeof product?.custom_fields !== 'undefined' && typeof product?.custom_fields['stamped-reviews_average'] !== 'undefined' && product?.custom_fields['stamped-reviews_average'] !== null && product?.custom_fields['stamped-reviews_average'] !== '') {
    reviews_average = product?.custom_fields['stamped-reviews_average'];
    
    if (reviews_average === '0' || reviews_average === '0.0' || reviews_average === '0.00') {
      reviews_average = null;
    }
  }

  return reviews_average
    ? `<a
        href="${product.url}#stamped-main-widget"
        class="product-tile__rating"
        data-product-tile-rating
        aria-label="Product rating ${Number(reviews_average).toFixed(1)} out of 5"
      >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.6842 10.1897L10.5874 12.5454L9.55157 8.10544L13 5.11807L8.45894 4.73281L6.6842 0.545441L4.90946 4.73281L0.368408 5.11807L3.81683 8.10544L2.78104 12.5454L6.6842 10.1897Z" fill="black" />
          </svg>
          <span>${Number(reviews_average).toFixed(1)}</span>
      </a>` : '';
};

const pricesForTile = (product, product_available) => {
  let lowest_price = 0;
  let highest_price = 0;
  product.skus.forEach((sku) => {
    lowest_price = (lowest_price === 0 || sku.price < lowest_price) ? sku.price : lowest_price;
    highest_price = (highest_price === 0 || sku.price > highest_price) ? sku.price : highest_price;
  });
  const from_price = (highest_price > lowest_price) ? String(parseFloat(lowest_price, 10)) : '';

  let price_html = '';
  if (from_price === '') {
    if (product.list_price > product.price) {
      price_html += `<span class="visually-hidden">Regular price</span><span class="price__sales"><s class="price__compare-at">${formatMoney(product.list_price_text, CONSTANTS.MONEY_FORMAT)}</s>${formatMoney(product.price_text, CONSTANTS.MONEY_FORMAT)}</span>`;
    } else {
      price_html += `<span class="price__regular">${formatMoney(product.price_text, CONSTANTS.MONEY_FORMAT)}</span>`;
    }
  } else {
    price_html += `<span class="price__regular">From ${formatMoney(from_price, CONSTANTS.MONEY_FORMAT)}</span>`;
  }

  if (!product_available) {
    price_html += `<span class="price__sold-out">Sold Out</span>`;
  }

  return price_html;
};

// tile_settings: {is_slider: true, is_simplified: true, has_swatches: false, has_quickview: false, is_search: false, is_lookbook: false}
    // Simplified tiles don't have the wishlist, flags, reviews, promo messages...
    // Lookbook tiles use a different grid layout
    // Quickview: tiles need to have the swatches
export default (product, tile_settings, debug = false) => {
  try {
    if (debug) {
      console.log(product);
    }

    if (typeof product.skus === 'undefined' || product.skus.length === 0) {
      console.log(`[ASTR] Product (id: ${product.product_id}) has no variants`);
      return;
    }

    const product_handle = product.url.split('/').pop();
    const safe_product_title = product_handle.replace(/-/g, ' ');
    const product_url = getProductURL(product_handle);
    const product_available = true;
    const has_plus_size = product.skus.some(sku => sku.name.includes('Plus Size -'));
    const product_tags = [...product.tags1 ?? [], ...product.tags2 ?? [], ...product.tags3 ?? []];
    const promos_html = (tile_settings.is_simplified === true) ? '' : promoForTile(product, tile_settings);
    const flags_html = (tile_settings.is_simplified === true) ? '' : flagsForTile(product, product_tags);
    const wishlist_html = (tile_settings.is_simplified === true) ? '' : wishlistForTile(product, product_handle);
    const images_html = imagesForTile(product, safe_product_title, tile_settings);
    const add_to_cart_alt = (tile_settings.is_simplified === true) ? '' : addToCartAltForTile(product);
    const quickview_html = (tile_settings.has_quickview) ? quickviewForTile() : '';
    const swatches_html = (tile_settings.has_swatches === false && tile_settings.has_quickview === false) ? '' : swatchesForTile(product);
    const ratings_html = (tile_settings.is_simplified === true) ? '' : ratingsForTile(product);
    const price_html = pricesForTile(product, product_available);

    return `<div class="product-tile${(tile_settings.is_slider === true) ? ' swiper-slide' : '' }${(tile_settings.is_lookbook === false && tile_settings.is_slider !== true) ? ' column-xs-6 column-sm-3' : '' }${tile_settings.has_animations !== false ? ' animate animate--fadeInUp ' : '' } ${(tile_settings.is_simplified === true && tile_settings.has_swatches === false && tile_settings.has_quickview === false) ? ' product-tile--simplified' : ''}"
        data-product-url="${product_url}"
        data-product-handle="${product_handle}"
        data-product-id="${product.product_id}"
        data-product-available="${product_available}"
        data-first-variant-id="${product.skus[0].id}"
        data-empi="${product.skus[0].id}"
        data-product-tile
        data-quickview="${tile_settings.has_quickview}"
        data-plus-size="${has_plus_size}"
        tabindex="0"
        aria-label="${safe_product_title}"
      >
        ${wishlist_html}

        <div class="product-tile__image-container">
          <a class="product-tile__image" href="${product_url}" data-product-tile-image aria-label="${product.name}">
            ${images_html}
            ${add_to_cart_alt}
            ${flags_html}
          </a>
          ${quickview_html}
        </div>

        <div class="product-tile__bottom">
          ${(tile_settings.has_swatches === false && tile_settings.has_quickview === false) ? '' : '<div class="product-tile__colors-rating">' }
            ${swatches_html}
            ${ratings_html}
          ${(tile_settings.has_swatches === false && tile_settings.has_quickview === false) ? '' : '</div>' }

          <p class="product-tile__title" data-product-tile-title>
            <a href="${product_url}">${product.name}</a>
          </p>

          <p class="product-tile__price price" data-product-tile-price>
            ${price_html}
          </p>

          ${promos_html}
        </div>
      </div>`;
  } catch (e) {
    console.error(e);
    console.error(product);
    return '';
  }
};
